import { app, TransitionConfig } from './core/App';
import { SceneAssets } from './core/modules/AssetManager';
import { RunSettings } from './level/RunSettingsHandler';
import { assetLookup, AssetPack } from './assets-to-load';

import Boot from './scenes/Boot';
import Title from './scenes/Title';
import PreloadCartoonito from './scenes/PreloadCartoonito';
import CharacterSelect from './scenes/CharacterSelect';
import LevelSelect from './scenes/LevelSelect';
import Game from './scenes/Game';
import TransitionSceneToScene from './scenes/TransitionSceneToScene';
import GameUI from './scenes/GameUI';
import DebugUI from './scenes/DebugUI';
import PreloadTomAndJerry from './scenes/PreloadTomAndJerry';
import TransitionLoadScene from './scenes/TransitionLoadScene';

export const SceneId = {
  Boot: 'Boot',
  PreloadCartoonito: 'PreloadCartoonito',
  PreloadTomAndJerry: 'PreloadTomAndJerry',
  Title: 'Title',
  CharacterSelect: 'CharacterSelect',
  LevelSelect: 'LevelSelect',
  Game: 'Game',
  GameUI: 'GameUI',
  TransitionSceneToScene: 'TransitionSceneToScene',
  TransitionLoadScene: 'TransitionLoadScene',
  DebugUI: 'DebugUI'
} as const;

export const TransitionPoint = {
  BootEnd: 'BootEnd',
  PreloadCartoonitoEnd: 'PreloadCartoonitoEnd',
  PreloadEnd: 'PreloadEnd',
  TitleEnd: 'TitleEnd',
  TitleEndFirstTime: 'TitleEndFirstTime',
  CharacterSelectBack: 'CharacterSelectBack',
  CharacterSelectEnd: 'CharacterSelectEnd',
  LevelSelectBack: 'LevelSelectBack',
  LevelSelectEnd: 'LevelSelectEnd',
  GameBack: 'GameBack',
  GameHome: 'GameHome',
  GameLevelSelect: 'GameLevelSelect',
  GameReplay: 'GameReplay'
} as const;

export const scenes = [Boot, PreloadTomAndJerry, Title, CharacterSelect, LevelSelect, Game, GameUI, TransitionSceneToScene, TransitionLoadScene, DebugUI];

// Scene Asset Dependencies.
const baseAssets: Set<string> = new Set([AssetPack.Common, AssetPack.Game32]);
const sceneAssetData: Map<keyof typeof SceneId, SceneAssets> = new Map();
sceneAssetData.set(SceneId.Title, { requiredAssets: [AssetPack.Title] });
sceneAssetData.set(SceneId.LevelSelect, { requiredAssets: [AssetPack.LevelSelect] });
sceneAssetData.set(SceneId.CharacterSelect, { requiredAssets: [AssetPack.CharacterSelect] });
sceneAssetData.set(SceneId.Game, { hasDynamicAssets: true, requiredAssets: [AssetPack.Game] });

// Routing table.
const sceneMap: Map<keyof typeof TransitionPoint, keyof typeof SceneId | TransitionConfig> = new Map();
// sceneMap.set(TransitionPoint.BootEnd, SceneId.PreloadCartoonito);
sceneMap.set(TransitionPoint.BootEnd, SceneId.PreloadTomAndJerry);
sceneMap.set(TransitionPoint.PreloadEnd, SceneId.Title);
sceneMap.set(TransitionPoint.TitleEnd, {
  nextScene: SceneId.LevelSelect,
  transitionScene: SceneId.TransitionLoadScene,
  transitionTime: 1000,
  waitForPreload: true
});
sceneMap.set(TransitionPoint.TitleEndFirstTime, {
  nextScene: SceneId.CharacterSelect,
  transitionScene: SceneId.TransitionLoadScene,
  transitionTime: 1000,
  waitForPreload: true
});
sceneMap.set(TransitionPoint.LevelSelectBack, {
  nextScene: SceneId.Title,
  transitionScene: SceneId.TransitionLoadScene,
  transitionTime: 1000,
  waitForPreload: true
});
sceneMap.set(TransitionPoint.LevelSelectEnd, {
  nextScene: SceneId.CharacterSelect,
  transitionScene: SceneId.TransitionLoadScene,
  transitionTime: 1000,
  waitForPreload: true
});
sceneMap.set(TransitionPoint.CharacterSelectBack, {
  nextScene: SceneId.LevelSelect,
  transitionScene: SceneId.TransitionLoadScene,
  transitionTime: 1000,
  waitForPreload: true
});
sceneMap.set(TransitionPoint.CharacterSelectEnd, {
  nextScene: SceneId.Game,
  transitionScene: SceneId.TransitionLoadScene,
  transitionTime: 1000,
  waitForPreload: true
});
sceneMap.set(TransitionPoint.GameBack, {
  nextScene: SceneId.LevelSelect,
  transitionScene: SceneId.TransitionLoadScene,
  transitionTime: 1000,
  waitForPreload: true
});
sceneMap.set(TransitionPoint.GameHome, {
  nextScene: SceneId.Title,
  transitionScene: SceneId.TransitionLoadScene,
  transitionTime: 1000,
  waitForPreload: true
});
sceneMap.set(TransitionPoint.GameLevelSelect, {
  nextScene: SceneId.LevelSelect,
  transitionScene: SceneId.TransitionLoadScene,
  transitionTime: 1000,
  waitForPreload: true
});
sceneMap.set(TransitionPoint.GameReplay, {
  nextScene: SceneId.Game,
  transitionScene: SceneId.TransitionSceneToScene,
  transitionTime: 1000,
  waitForPreload: true
});

app.updateSceneAssetMaps(assetLookup, baseAssets, sceneAssetData);
app.updateSceneMap(sceneMap as unknown as Map<string, string>, TransitionPoint.PreloadEnd.toString());

export interface SceneData {
  sourceScene: keyof typeof SceneId;
  sourceTransition?: keyof typeof TransitionPoint;
  transitionData?: TransitionSceneData;
  gameSceneData?: GameSceneData;
  runSettings?: Partial<RunSettings>;
  [key: string]: any;
}

export interface TransitionSceneData {
  sourceScene: string;
  nextScene: string;
}

export interface GameSceneData {
  shouldSkip: boolean;
}
